import React, { useState, useEffect } from 'react';
import { contactAPI } from '../../../services/api';
import '../Shared/FormStyles.scss';

const STATUS_OPTIONS = ['new', 'read', 'replied'];

const ContactInquiriesManager = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      const response = await contactAPI.getAll();
      setInquiries(response.data);
    } catch (err) {
      setError('Failed to fetch inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await contactAPI.updateStatus(id, status);
      setSuccess('Status updated');
      fetchInquiries();
      if (selected?._id === id) {
        setSelected((prev) => ({ ...prev, status }));
      }
    } catch (err) {
      setError('Failed to update status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this inquiry? This cannot be undone.')) return;
    try {
      await contactAPI.delete(id);
      setSuccess('Inquiry deleted');
      if (selected?._id === id) setSelected(null);
      fetchInquiries();
    } catch (err) {
      setError('Failed to delete inquiry');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const statusBadge = (status) => {
    const colors = { new: '#e74c3c', read: '#f39c12', replied: '#27ae60' };
    return (
      <span style={{
        background: colors[status] || '#999',
        color: '#fff',
        padding: '2px 10px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
      }}>
        {status}
      </span>
    );
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="contact-inquiries-manager">
      {error && <div className="error-message" style={{ margin: '0 0 16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ margin: '0 0 16px' }}>{success}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 1fr' : '1fr', gap: '24px' }}>
        <div className="data-table">
          <h2 style={{ padding: '20px', margin: 0 }}>Contact Inquiries ({inquiries.length})</h2>
          {inquiries.length === 0 ? (
            <div className="empty-state">No inquiries yet.</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inq) => (
                  <tr
                    key={inq._id}
                    style={{ cursor: 'pointer', background: selected?._id === inq._id ? '#f0f7ff' : undefined }}
                    onClick={() => setSelected(inq)}
                  >
                    <td style={{ whiteSpace: 'nowrap', fontSize: '13px' }}>{formatDate(inq.createdAt)}</td>
                    <td>{inq.name}</td>
                    <td style={{ fontSize: '13px' }}>{inq.email}</td>
                    <td>{statusBadge(inq.status || 'new')}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="table-actions">
                        <select
                          value={inq.status || 'new'}
                          onChange={(e) => handleStatusChange(inq._id, e.target.value)}
                          style={{ fontSize: '13px', padding: '4px 8px', borderRadius: '4px', border: '1px solid #ddd' }}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                        <button className="btn-delete" onClick={() => handleDelete(inq._id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {selected && (
          <div className="admin-form" style={{ alignSelf: 'start' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0 }}>Inquiry Detail</h2>
              <button
                onClick={() => setSelected(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#999' }}
              >
                ×
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div><strong>Date:</strong> {formatDate(selected.createdAt)}</div>
              <div><strong>Status:</strong> {statusBadge(selected.status || 'new')}</div>
              <div><strong>Name:</strong> {selected.name}</div>
              <div><strong>Email:</strong> <a href={`mailto:${selected.email}`}>{selected.email}</a></div>
              {selected.phone && <div><strong>Phone:</strong> <a href={`tel:${selected.phone}`}>{selected.phone}</a></div>}
              <div>
                <strong>Message:</strong>
                <p style={{ marginTop: '8px', whiteSpace: 'pre-wrap', background: '#f8f8f8', padding: '12px', borderRadius: '6px', lineHeight: 1.6 }}>
                  {selected.message}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {STATUS_OPTIONS.map((s) => (
                  <button
                    key={s}
                    className={`dashboard-btn ${selected.status === s ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handleStatusChange(selected._id, s)}
                    style={{ textTransform: 'capitalize' }}
                  >
                    Mark {s}
                  </button>
                ))}
              </div>
              <a
                href={`mailto:${selected.email}?subject=Re: Your enquiry to Integrity Physio`}
                className="dashboard-btn btn-primary"
                style={{ textAlign: 'center', textDecoration: 'none' }}
              >
                Reply via Email
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactInquiriesManager;
